/**
 * Professional read-only condition visualizer — matches ViewSchemeModal design language.
 * Sentence-style display with muted chips and a subtle time-window bar for historical checks.
 */
import React from "react";
import { FiActivity, FiLayers } from "react-icons/fi";

const OP_LABEL = {
  eq: "equals", neq: "is not", ne: "is not",
  gt: "is more than", gte: "at least",
  lt: "is less than", lte: "at most",
  in_list: "is one of", not_in_list: "is not in",
  in: "is one of", not_in: "is not in",
};

const HIST_OP_LABEL = {
  gte: "at least", gt: "more than", lte: "at most", lt: "fewer than",
  eq: "exactly", neq: "not exactly",
  first_N_scans: "first N scans",
  active_streak: "active streak",
  all_of: "all of",
};

const OP_CHIP_CLS = {
  gt: "bg-emerald-50 text-emerald-700 border-emerald-200",
  gte: "bg-emerald-50 text-emerald-700 border-emerald-200",
  lt: "bg-rose-50 text-rose-600 border-rose-200",
  lte: "bg-rose-50 text-rose-600 border-rose-200",
  eq: "bg-blue-50 text-blue-600 border-blue-200",
  neq: "bg-amber-50 text-amber-700 border-amber-200",
  ne: "bg-amber-50 text-amber-700 border-amber-200",
  in_list: "bg-indigo-50 text-indigo-600 border-indigo-200",
  not_in_list: "bg-orange-50 text-orange-700 border-orange-200",
  in: "bg-indigo-50 text-indigo-600 border-indigo-200",
  not_in: "bg-orange-50 text-orange-700 border-orange-200",
  first_N_scans: "bg-violet-50 text-violet-700 border-violet-200",
  active_streak: "bg-violet-50 text-violet-700 border-violet-200",
  all_of: "bg-teal-50 text-teal-700 border-teal-200",
};

function OpChip({ op }) {
  const cls = OP_CHIP_CLS[op] || "bg-gray-100 text-gray-600 border-gray-200";
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-semibold border ${cls}`}>
      {OP_LABEL[op] || op}
    </span>
  );
}

function HistOpChip({ op }) {
  const cls = OP_CHIP_CLS[op] || "bg-gray-100 text-gray-600 border-gray-200";
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-semibold border ${cls}`}>
      {HIST_OP_LABEL[op] || op}
    </span>
  );
}

function ValueDisplay({ value, fieldKey, wallets }) {
  const isSchemeKey = fieldKey === "metadata.scheme_id" || fieldKey === "metadata.scheme_ids";
  const resolve = (id) =>
    (wallets || []).find((w) => String(w.scheme_id) === String(id))?.name || `Scheme #${id}`;

  if (Array.isArray(value)) {
    const labels = isSchemeKey ? value.map(resolve) : value.map(String);
    return (
      <span className="inline-flex flex-wrap gap-1">
        {labels.map((v, i) => (
          <span key={i} className="inline-flex items-center px-2 py-0.5 rounded bg-gray-100 text-gray-700 border border-gray-200 text-[11px] font-medium">
            {v}
          </span>
        ))}
      </span>
    );
  }

  const display = isSchemeKey ? resolve(value) : String(value ?? "—");
  return <span className="text-sm text-gray-900 font-medium">{display}</span>;
}

function TimeBar({ days }) {
  const pct = Math.min(100, Math.round((days / 365) * 100));
  return (
    <div className="flex items-center gap-2 mt-1.5">
      <div className="flex-1 h-1 bg-gray-200 rounded-full overflow-hidden max-w-[120px]">
        <div className="h-full bg-violet-400 rounded-full" style={{ width: `${pct}%` }} />
      </div>
      <span className="text-[10px] text-gray-400">past {days} days</span>
    </div>
  );
}

// ─── Fact Check ───────────────────────────────────────────────────────────────

function FactCheckLeaf({ condition, meta, wallets }) {
  const def = meta?.fact_check_keys?.[condition.key];
  const fieldLabel = def?.label || condition.key || "—";

  return (
    <div className="py-2 px-3">
      <div className="flex items-center gap-1.5 mb-0.5">
        <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-blue-500 uppercase tracking-wider">
          <FiActivity className="h-3 w-3" /> Fact Check
        </span>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm text-gray-700 font-medium">{fieldLabel}</span>
        <OpChip op={condition.operator} />
        <ValueDisplay value={condition.value} fieldKey={condition.key} wallets={wallets} />
      </div>
      {def?.meaning && (
        <p className="text-[11px] text-gray-400 mt-0.5 leading-relaxed">{def.meaning}</p>
      )}
    </div>
  );
}

// ─── Historical Aggregate ─────────────────────────────────────────────────────

function HistoricalLeaf({ condition, meta }) {
  const def = meta?.historical_keys?.[condition.tracking_key];
  const metricLabel = def?.label || condition.tracking_key || "—";
  const isAdvanced = ["first_N_scans", "active_streak", "all_of"].includes(condition.operator);
  const aggLabel = condition.aggregate_type === "sum" ? "sum" : "count";

  const valueDisplay = Array.isArray(condition.value)
    ? condition.value.join(", ")
    : String(condition.value ?? "—");

  return (
    <div className="py-2 px-3">
      <div className="flex items-center gap-1 mb-0.5">
        <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-violet-500 uppercase tracking-wider">
          <FiLayers className="h-3 w-3" /> History Check
        </span>
        <span className="text-[10px] text-gray-300">·</span>
        <span className="text-[10px] text-gray-400">{aggLabel}</span>
      </div>

      {condition.operator === "all_of" ? (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-gray-700 font-medium">{metricLabel}</span>
          <HistOpChip op={condition.operator} />
          <span className="inline-flex flex-wrap gap-1">
            {(Array.isArray(condition.value) ? condition.value : []).map((v, i) => (
              <span key={i} className="inline-flex items-center px-2 py-0.5 rounded bg-teal-50 text-teal-700 border border-teal-200 text-[11px] font-medium">{v}</span>
            ))}
          </span>
        </div>
      ) : (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-gray-700 font-medium">{metricLabel}</span>
          <HistOpChip op={condition.operator} />
          <span className="text-sm text-gray-900 font-medium">{valueDisplay}</span>
          {!isAdvanced && condition.time_window_days && (
            <span className="text-[11px] text-gray-400">in the last {condition.time_window_days} days</span>
          )}
        </div>
      )}

      {!isAdvanced && condition.time_window_days > 0 && (
        <TimeBar days={condition.time_window_days} />
      )}

      {Array.isArray(condition.source_type_filter) && condition.source_type_filter.length > 0 && (
        <div className="flex items-center gap-1 mt-1.5">
          <span className="text-[10px] text-gray-400">Source:</span>
          {condition.source_type_filter.map((s) => (
            <span key={s} className="inline-flex items-center px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 border border-gray-200 text-[10px] font-medium">{s}</span>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Compound Group ───────────────────────────────────────────────────────────

function GroupNode({ condition, meta, wallets, depth }) {
  const isAnd = condition.type === "AND";
  const children = condition.children || [];

  return (
    <div className={`rounded-lg border overflow-hidden ${depth % 2 === 0 ? "border-gray-200 bg-white" : "border-gray-200 bg-gray-50"}`}>
      {/* Gate header */}
      <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border-b border-gray-200">
        <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold border uppercase tracking-wider
          ${isAnd ? "bg-gray-900 text-white border-gray-900" : "bg-gray-700 text-white border-gray-700"}`}>
          {isAnd ? "ALL OF" : "ANY OF"}
        </span>
        <span className="text-[11px] text-gray-400">
          {isAnd ? "every condition must pass" : "any condition is enough"}
        </span>
        <span className="ml-auto text-[10px] text-gray-400">{children.length} rule{children.length !== 1 ? "s" : ""}</span>
      </div>

      {/* Children */}
      <div className="divide-y divide-gray-100">
        {children.map((child, i) => (
          <div key={i} className="pl-4 border-l-2 border-gray-200 ml-3 mr-0">
            <ViewCondition condition={child} meta={meta} wallets={wallets} depth={depth + 1} />
          </div>
        ))}
        {children.length === 0 && (
          <p className="px-3 py-2 text-xs text-gray-400 italic">No conditions.</p>
        )}
      </div>
    </div>
  );
}

// ─── Root ─────────────────────────────────────────────────────────────────────

export default function ViewCondition({ condition, meta, wallets, depth = 0 }) {
  if (!condition) return null;
  if (condition.type === "AND" || condition.type === "OR")
    return <GroupNode condition={condition} meta={meta} wallets={wallets} depth={depth} />;
  if (condition.type === "fact_check")
    return <FactCheckLeaf condition={condition} meta={meta} wallets={wallets} />;
  if (condition.type === "historical_aggregate")
    return <HistoricalLeaf condition={condition} meta={meta} />;
  return (
    <div className="px-3 py-2 text-xs text-gray-400 italic">Unknown condition type: {condition.type}</div>
  );
}
